import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Button,
  Row,
  Col,
  Badge,
  Accordion,
  Spinner,
  ProgressBar,
  Table,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  getFirestore,
  query,
  where,
} from "firebase/firestore";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaGraduationCap,
  FaArrowLeft,
  FaEdit,
  FaBook,
  FaVideo,
  FaFilePdf,
  FaQuestionCircle,
  FaUsers,
  FaChartLine,
  FaCheckCircle,
  FaClock,
  FaYoutube,
  FaCalendarAlt,
  FaFileExcel,
  FaUserGraduate,
} from "react-icons/fa";
import { BsCheckCircleFill, BsXCircleFill } from "react-icons/bs";
import * as XLSX from "xlsx";
import firebaseApp from "../../firebase/credenciales";

const db = getFirestore(firebaseApp);

const formatearFecha = (timestamp) => {
  if (!timestamp) return "No disponible";
  const date = new Date(
    timestamp.seconds ? timestamp.seconds * 1000 : timestamp
  );
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const StatCard = ({ icon, label, value, color, sublabel }) => (
  <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "15px" }}>
    <Card.Body className="text-center p-3">
      <div className="mb-2" style={{ color }}>{icon}</div>
      <h4 className="fw-bold mb-0">{value}</h4>
      <small className="text-muted d-block">{label}</small>
      {sublabel && <small className="text-muted opacity-75 d-block" style={{ fontSize: '0.7rem' }}>{sublabel}</small>}
    </Card.Body>
  </Card>
);

const CursosPage = ({ usuario }) => {
  const { cursoId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [curso, setCurso] = useState(null);
  const [estadisticas, setEstadisticas] = useState({
    totalInscritos: 0,
    completados: 0,
    enProgreso: 0,
    capacitados: 0,
    porcentajeCompletado: 0,
    totalHorasHombre: 0,
  });
  const [progresosPorModulo, setProgresosPorModulo] = useState([]);
  const [usuariosDetalle, setUsuariosDetalle] = useState([]);
  const [documentosPorDni, setDocumentosPorDni] = useState({});

  useEffect(() => {
    if (usuario?.ruc && cursoId) {
      cargarDatosCurso();
    }
  }, [usuario, cursoId]);

  const cargarDatosCurso = async () => {
    try {
      setLoading(true);
      const rucEmpresa = usuario?.ruc || "";

      // Cargar información del curso
      const cursoRef = doc(
        db,
        `in_capacitacion/${rucEmpresa}/programas`,
        cursoId
      );
      const cursoSnap = await getDoc(cursoRef);

      if (!cursoSnap.exists()) {
        console.error("Curso no encontrado");
        navigate("/capacitaciones");
        return;
      }

      const cursoData = {
        id: cursoSnap.id,
        ...cursoSnap.data(),
      };
      
      setCurso(cursoData);

      // Cargar asignaciones para obtener usuarios inscritos
      const asignacionesRef = collection(
        db,
        `in_capacitacion/${rucEmpresa}/asignaciones`
      );
      const asignacionesSnapshot = await getDocs(asignacionesRef);
      const usuariosInscritosCurso = new Set();

      asignacionesSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.cursoId === cursoId) {
          usuariosInscritosCurso.add(data.dni);
        }
      });

      // Cargar progresos de usuarios
      const progresosRef = collection(
        db,
        `in_capacitacion/${rucEmpresa}/progresos`
      );
      const progresosSnapshot = await getDocs(progresosRef);
      const progresosData = [];

      progresosSnapshot.forEach((doc) => {
        const data = doc.data();
        progresosData.push({
          dni: doc.id,
          modulosCompletados: data.modulosCompletados || [],
        });
      });

      // Cargar nombres de usuarios
      const usuariosRef = collection(db, "users");
      const usuariosSnapshot = await getDocs(usuariosRef);
      const nombresUsuarios = {};
      usuariosSnapshot.forEach((doc) => {
        const data = doc.data();
        const dni = String(data.dni || doc.id || "");
        if (!dni) return;
        const nombre = `${data.name || data.nombre || ""} ${data.apellidos || ""}`.trim();
        nombresUsuarios[dni] = nombre || dni;
      });

      // Cargar respuestas para este curso
      const respuestasRef = collection(db, `in_capacitacion/${rucEmpresa}/respuestas`);
      const qRespuestas = query(respuestasRef, where("cursoId", "==", cursoId));
      const respuestasSnapshot = await getDocs(qRespuestas);
      const respuestasData = [];
      respuestasSnapshot.forEach((doc) => {
        respuestasData.push(doc.data());
      });

      // Cargar documentos PDF (asistencia/certificado) para este curso
      const docsCapRef = collection(
        db,
        `in_capacitacion/${rucEmpresa}/documentos_capacitacion`
      );
      const qDocsCap = query(docsCapRef, where("cursoId", "==", cursoId));
      const docsCapSnap = await getDocs(qDocsCap);
      const mapaDocs = {};
      docsCapSnap.forEach((d) => {
        const data = d.data() || {};
        if (!data.dni || !data.tipoDocumento || !data.url) return;
        if (data.visibleParaAdmin === false) return;
        const dni = String(data.dni);
        if (!mapaDocs[dni]) {
          mapaDocs[dni] = {};
        }
        mapaDocs[dni][data.tipoDocumento] = {
          url: data.url,
          storagePath: data.storagePath || "",
          estadoRegistro: data.estadoRegistro || "",
          actualizadoEn: data.actualizadoEn || null,
        };
      });
      setDocumentosPorDni(mapaDocs);

      // Complementar universo de participantes con actividad real del curso
      // (progreso, respuestas o documentos), incluso si no hay asignación.
      progresosData.forEach((progreso) => {
        if (!Array.isArray(progreso.modulosCompletados)) return;
        const tieneActividad = progreso.modulosCompletados.some((m) =>
          String(m).startsWith(`${cursoId}_`)
        );
        if (tieneActividad) {
          usuariosInscritosCurso.add(progreso.dni);
        }
      });
      respuestasData.forEach((r) => {
        if (r?.dni && r?.cursoId === cursoId) {
          usuariosInscritosCurso.add(String(r.dni));
        }
      });
      Object.keys(mapaDocs).forEach((dni) => {
        usuariosInscritosCurso.add(String(dni));
      });

      // Calcular estadísticas
      const usuariosCompletados = new Set();
      const usuariosEnProgreso = new Set();
      const detalleUsuarios = [];

      // Inicializar con todos los inscritos
      usuariosInscritosCurso.forEach((dni) => {
        detalleUsuarios.push({
          dni: dni,
          nombre: nombresUsuarios[dni] || dni,
          modulosCompletados: 0,
          totalModulos: cursoData.modulos?.length || 0,
          porcentaje: 0,
          estado: "INSCRITO",
        });
      });

      // Actualizar con progreso
      progresosData.forEach((progreso) => {
        if (!Array.isArray(progreso.modulosCompletados)) return;
        
        const modulosDelCurso = progreso.modulosCompletados.filter((m) =>
          m.startsWith(`${cursoId}_`)
        );

        if (modulosDelCurso.length > 0) {
          const totalModulos = cursoData.modulos?.length || 0;
          const modulosCompletadosCurso = modulosDelCurso.length;
          const porcentaje = totalModulos > 0 ? (modulosCompletadosCurso / totalModulos) * 100 : 0;

          if (modulosCompletadosCurso === totalModulos && totalModulos > 0) {
            usuariosCompletados.add(progreso.dni);
          } else {
            usuariosEnProgreso.add(progreso.dni);
          }

          const indexUsuario = detalleUsuarios.findIndex((u) => u.dni === progreso.dni);
          const detalleUsuario = {
            dni: progreso.dni,
            nombre: nombresUsuarios[progreso.dni] || progreso.dni,
            modulosCompletados: modulosCompletadosCurso,
            totalModulos: totalModulos,
            porcentaje: porcentaje,
            estado: modulosCompletadosCurso === totalModulos && totalModulos > 0 ? "COMPLETADO" : "EN PROGRESO",
          };

          if (indexUsuario >= 0) {
            detalleUsuarios[indexUsuario] = detalleUsuario;
          } else {
            detalleUsuarios.push(detalleUsuario);
          }
        }
      });

      // Reconciliar estado con documentos generados:
      // si existe certificado, consideramos el curso completado.
      detalleUsuarios.forEach((u, idx) => {
        const docCert = mapaDocs?.[u.dni]?.certificado;
        const docAsis = mapaDocs?.[u.dni]?.asistencia;
        if (docCert?.url) {
          detalleUsuarios[idx] = {
            ...u,
            modulosCompletados: u.totalModulos > 0 ? u.totalModulos : u.modulosCompletados,
            porcentaje: 100,
            estado: "COMPLETADO",
          };
          usuariosCompletados.add(u.dni);
          usuariosEnProgreso.delete(u.dni);
          return;
        }
        if ((u.porcentaje || 0) > 0 || docAsis?.url) {
          usuariosEnProgreso.add(u.dni);
        }
      });

      const totalInscritos = detalleUsuarios.length;
      const totalCompletados = detalleUsuarios.filter((u) => u.estado === "COMPLETADO").length;
      const totalEnProgreso = detalleUsuarios.filter((u) => u.estado === "EN PROGRESO").length;
      const totalCapacitados = detalleUsuarios.filter(
        (u) =>
          (u.porcentaje || 0) > 0 ||
          mapaDocs?.[u.dni]?.asistencia?.url ||
          mapaDocs?.[u.dni]?.certificado?.url
      ).length;

      setEstadisticas({
        totalInscritos,
        completados: totalCompletados,
        enProgreso: totalEnProgreso,
        porcentajeCompletado: totalInscritos > 0 ? (totalCompletados / totalInscritos) * 100 : 0,
        capacitados: totalCapacitados,
        totalHorasHombre: totalCompletados * (cursoData.duracionHoras || 1),
      });

      setUsuariosDetalle(detalleUsuarios);

      let progresoModulos = cursoData.modulos?.map((modulo, index) => {
        let usuariosCompletaron = 0;
        progresosData.forEach((progreso) => {
          if (!Array.isArray(progreso.modulosCompletados) || !usuariosInscritosCurso.has(progreso.dni)) return;
          const moduloId = `${cursoId}_${index}`;
          if (progreso.modulosCompletados.includes(moduloId)) usuariosCompletaron++;
        });

        return {
          titulo: modulo.titulo,
          completados: usuariosCompletaron,
          total: usuariosInscritosCurso.size,
          porcentaje: usuariosInscritosCurso.size > 0 ? (usuariosCompletaron / usuariosInscritosCurso.size) * 100 : 0,
        };
      });

      // Fallback: si no hay trazabilidad por módulo pero sí completados por certificado,
      // mostramos avance equivalente en todos los módulos para reflejar progreso real.
      const sinDataModulo =
        Array.isArray(progresoModulos) &&
        progresoModulos.length > 0 &&
        progresoModulos.every((m) => (m.completados || 0) === 0);
      if (sinDataModulo && totalCompletados > 0 && totalInscritos > 0) {
        progresoModulos = progresoModulos.map((m) => ({
          ...m,
          completados: totalCompletados,
          total: totalInscritos,
          porcentaje: (totalCompletados / totalInscritos) * 100,
        }));
      }

      setProgresosPorModulo(progresoModulos || []);
    } catch (error) {
      console.error("Error al cargar datos del curso:", error);
    } finally {
      setLoading(false);
    }
  };

  const exportarDetalleExcel = () => {
    try {
      const datosExcel = usuariosDetalle.map((u, index) => ({
        "DNI": u.dni,
        "N°": index + 1,
        "Participante": u.nombre,
        "Estado": u.estado,
        "Progreso %": u.porcentaje.toFixed(1) + "%",
        "Módulos": `${u.modulosCompletados}/${u.totalModulos}`,
        "PDF Asistencia": documentosPorDni?.[u.dni]?.asistencia?.url || "",
        "PDF Certificado": documentosPorDni?.[u.dni]?.certificado?.url || "",
      }));

      const ws = XLSX.utils.json_to_sheet(datosExcel);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Detalle Participantes");
      XLSX.writeFile(wb, `Detalle_Curso_${curso?.nombre}_${cursoId}.xlsx`);
    } catch (error) {
      console.error("Error al exportar Excel:", error);
      alert("Error al exportar los datos");
    }
  };

  const getBadgeEstado = (estado) => {
    switch (estado) {
      case "COMPLETADO": return "success";
      case "EN PROGRESO": return "warning";
      case "INSCRITO": return "info";
      default: return "secondary";
    }
  };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex justify-content-center align-items-center" style={{ background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)" }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!curso) {
    return (
      <div className="min-vh-100 d-flex justify-content-center align-items-center" style={{ background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)" }}>
        <Card className="p-5 text-center shadow">
          <FaBook size={50} className="text-muted mb-3 mx-auto" />
          <h3>Curso no encontrado</h3>
          <Button variant="primary" onClick={() => navigate("/capacitaciones")}>Volver</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)", padding: "20px 0" }}>
      <Container fluid style={{ width: "95%", maxWidth: "100%" }}>
        <Card className="shadow-lg border-0" style={{ borderRadius: "15px", overflow: "hidden" }}>
          <Card.Header className="text-white py-4" style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
            <h2 className="fw-bold text-center mb-0"><FaGraduationCap className="me-3" />Detalles del Curso</h2>
          </Card.Header>
          
          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Action Bar */}
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-3">
                <Row className="align-items-center">
                  <Col md={1} className="text-center d-none d-md-block">
                    <div className="p-2 rounded-3 bg-primary text-white"><FaBook size={24} /></div>
                  </Col>
                  <Col md={7}>
                    <h4 className="mb-0 fw-bold">{curso.nombre} <Badge bg="light" text="dark" className="ms-2 border">{curso.categoria || "General"}</Badge></h4>
                    <p className="text-muted mb-0 small">{curso.descripcion}</p>
                  </Col>
                  <Col md={4} className="text-end">
                    <div className="d-flex gap-2 justify-content-end">
                      <Button variant="outline-primary" size="sm" onClick={() => navigate("/capacitaciones")}><FaArrowLeft className="me-1" /> Volver</Button>
                      <Button variant="outline-success" size="sm" onClick={exportarDetalleExcel}><FaFileExcel className="me-1" /> Excel</Button>
                      <Button variant="primary" size="sm" onClick={() => navigate(`/capacitaciones/editar-curso/${cursoId}`)}><FaEdit className="me-1" /> Editar</Button>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Row>
              {/* Left Column: Info and Modules */}
              <Col lg={8}>
                {/* General Info */}
                <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
                  <Card.Header className="bg-white py-3 border-bottom-0"><h5 className="mb-0 fw-bold"><FaBook className="me-2 text-primary" />Información General</h5></Card.Header>
                  <Card.Body>
                    <Row className="text-center">
                      <Col xs={4}>
                        <small className="text-muted d-block">Creado el</small>
                        <span className="fw-bold"><FaCalendarAlt className="me-1 text-primary" /> {formatearFecha(curso.fechaCreacion)}</span>
                      </Col>
                      <Col xs={4}>
                        <small className="text-muted d-block">Módulos</small>
                        <span className="fw-bold"><FaBook className="me-1 text-primary" /> {curso.modulos?.length || 0}</span>
                      </Col>
                      <Col xs={4}>
                        <small className="text-muted d-block">Duración Est.</small>
                        <span className="fw-bold"><FaClock className="me-1 text-primary" /> {curso.duracionHoras || 1}h</span>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* Modules Accordion */}
                <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
                  <Card.Header className="bg-white py-3 border-bottom-0"><h5 className="mb-0 fw-bold"><FaVideo className="me-2 text-primary" />Contenido del Curso</h5></Card.Header>
                  <Card.Body>
                    <Accordion defaultActiveKey="0">
                      {curso.modulos?.map((modulo, idx) => (
                        <Accordion.Item eventKey={idx.toString()} key={idx} className="border-0 mb-2 shadow-sm rounded">
                          <Accordion.Header>
                            <span className="fw-bold d-flex align-items-center"><Badge bg="primary" className="me-2">{idx + 1}</Badge> {modulo.titulo}</span>
                          </Accordion.Header>
                          <Accordion.Body className="bg-light rounded-bottom">
                            <p className="small text-muted mb-3">{modulo.descripcion}</p>
                            <div className="d-flex gap-2">
                              {modulo.videoURL && <Button variant="outline-danger" size="sm" href={modulo.videoURL} target="_blank"><FaYoutube className="me-1" /> Video</Button>}
                              {modulo.pdfURL && <Button variant="outline-primary" size="sm" href={modulo.pdfURL} target="_blank"><FaFilePdf className="me-1" /> Material PDF</Button>}
                              {modulo.preguntas?.length > 0 && <Badge bg="info" className="p-2 d-flex align-items-center"><FaQuestionCircle className="me-1" /> {modulo.preguntas.length} Preguntas</Badge>}
                            </div>
                          </Accordion.Body>
                        </Accordion.Item>
                      )) || <div className="text-center p-4 text-muted">No hay módulos disponibles</div>}
                    </Accordion>
                  </Card.Body>
                </Card>
              </Col>

              {/* Right Column: Stats and Participation */}
              <Col lg={4}>
                {/* Stats Cards Row */}
                <Row className="g-3 mb-4">
                  <Col xs={6}><StatCard icon={<FaUsers size={20} />} label="Inscritos" value={estadisticas.totalInscritos} color="#667eea" /></Col>
                  <Col xs={6}><StatCard icon={<FaCheckCircle size={20} />} label="Completados" value={estadisticas.completados} color="#28a745" /></Col>
                  <Col xs={6}><StatCard icon={<FaUserGraduate size={20} />} label="Capacitados" value={estadisticas.capacitados} color="#6610f2" sublabel="Engagement > 0%" /></Col>
                  <Col xs={6}><StatCard icon={<FaClock size={20} />} label="Horas Hombre" value={estadisticas.totalHorasHombre.toFixed(0)} color="#6f42c1" sublabel="Total acumulado" /></Col>
                </Row>

                {/* Progress per Module */}
                <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
                  <Card.Header className="bg-white py-3 border-bottom-0"><h5 className="mb-0 fw-bold"><FaChartLine className="me-2 text-primary" />Progreso por Módulo</h5></Card.Header>
                  <Card.Body className="p-3">
                    {progresosPorModulo.map((m, i) => (
                      <div key={i} className="mb-2">
                        <div className="d-flex justify-content-between small mb-1">
                          <span className="text-truncate" style={{maxWidth: '150px'}}>{m.titulo}</span>
                          <span className="fw-bold">{m.porcentaje.toFixed(0)}%</span>
                        </div>
                        <ProgressBar now={m.porcentaje} variant="success" style={{ height: "6px" }} />
                      </div>
                    ))}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Participants Tracking Table */}
            <Card className="shadow-sm border-0" style={{ borderRadius: "15px" }}>
              <Card.Header className="text-white py-3" style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", borderRadius: "15px 15px 0 0" }}>
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 fw-bold"><FaUsers className="me-2" />Seguimiento de Participantes</h5>
                  <Badge bg="light" text="dark">{usuariosDetalle.length} Participantes</Badge>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                <Table hover responsive className="mb-0 align-middle">
                  <thead className="bg-light small text-uppercase">
                    <tr>
                      <th className="ps-4">Participante</th>
                      <th className="text-center">Progreso</th>
                      <th className="text-center">Estado</th>
                      <th className="text-center">Asistencia</th>
                      <th className="text-center">Certificado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usuariosDetalle.sort((a,b) => b.porcentaje - a.porcentaje).map((u, i) => (
                      <tr key={i}>
                        <td className="ps-4 py-3">
                          <div className="fw-bold">{u.nombre}</div>
                          <small className="text-muted">{u.dni}</small>
                        </td>
                        <td style={{ width: "30%" }}>
                          <div className="d-flex align-items-center px-3">
                            <ProgressBar now={u.porcentaje} variant={u.porcentaje === 100 ? "success" : "info"} style={{ height: "8px", flexGrow: 1 }} />
                            <small className="ms-2 fw-bold">{u.porcentaje.toFixed(0)}%</small>
                          </div>
                        </td>
                        <td className="text-center">
                          <Badge pill bg={getBadgeEstado(u.estado)}>{u.estado}</Badge>
                        </td>
                        <td className="text-center">
                          {documentosPorDni?.[u.dni]?.asistencia?.url ? (
                            <OverlayTrigger overlay={<Tooltip>Ver registro de asistencia</Tooltip>}>
                              <Button
                                variant="link"
                                className="p-0 text-primary"
                                href={documentosPorDni[u.dni].asistencia.url}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <FaFilePdf size={18} />
                              </Button>
                            </OverlayTrigger>
                          ) : (
                            <small className="text-muted">No disponible</small>
                          )}
                        </td>
                        <td className="text-center">
                          {documentosPorDni?.[u.dni]?.certificado?.url ? (
                            <OverlayTrigger overlay={<Tooltip>Ver certificado</Tooltip>}>
                              <Button
                                variant="link"
                                className="p-0 text-success"
                                href={documentosPorDni[u.dni].certificado.url}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <FaFilePdf size={18} />
                              </Button>
                            </OverlayTrigger>
                          ) : (
                            <small className="text-muted">No disponible</small>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default CursosPage;
